<template>
    <div class="cs-page">
        <!-- ─────────────────── HEADER ─────────────────── -->
        <header class="cs-header">
            <div class="cs-header__inner">
                <div class="cs-header__title-wrap">
                    <h1 class="cs-title">לקוחות</h1>
                    <span class="cs-total-pill" :title="`סה״כ ${totalItems} לקוחות`">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                        <span>{{ totalItems.toLocaleString('he-IL') }}</span>
                    </span>
                </div>
                <div v-if="filterType !== 'all'" class="cs-filter-summary">
                    <span class="cs-filter-summary__count">{{ totalFilterManuimsCount.toLocaleString('he-IL') }}</span>
                    <span class="cs-filter-summary__label">מנויים בסינון</span>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <button v-on="on" class="cs-export-btn" @click="exportFiltersManuim"
                                aria-label="ייצוא לאקסל">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"
                                    aria-hidden="true">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                            </button>
                        </template>
                        <span>ייצוא לאקסל</span>
                    </v-tooltip>
                </div>
            </div>
        </header>

        <!-- ─────────────────── MAIN ─────────────────── -->
        <main class="cs-main">

            <!-- TOOLBAR -->
            <div class="cs-toolbar">
                <div class="cs-search">
                    <svg class="cs-search__icon" width="18" height="18" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"
                        aria-hidden="true">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input type="text" class="cs-search__input" placeholder="חיפוש לקוח לפי שם, ת״ז, טלפון…"
                        :value="search" @input="search = $event.target.value; handleSearchInput()" />
                    <button v-if="search" class="cs-search__clear" type="button"
                        @click="search = ''; handleSearchInput()" aria-label="נקה חיפוש">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                <div class="cs-filter-chips" role="tablist" aria-label="סינון לקוחות">
                    <button v-for="t in typesList" :key="t.value" type="button" role="tab" class="cs-chip"
                        :class="{ 'cs-chip--active': filterType === t.value }" :aria-selected="filterType === t.value"
                        @click="filterType = t.value; getCustomers()">
                        <span class="cs-chip__dot"></span>
                        {{ t.hebrewName }}
                    </button>
                </div>

                <div class="cs-pagesize">
                    <label for="cs-pagesize-select">שורות</label>
                    <select id="cs-pagesize-select" class="cs-pagesize__select" v-model.number="itemsPerPage"
                        @change="onItemsPerPageChange(itemsPerPage)">
                        <option v-for="n in [20, 50, 100, 500, 1000]" :key="n" :value="n">{{ n }}</option>
                    </select>
                </div>
            </div>

            <!-- TABLE -->
            <div class="cs-table-card">
                <v-data-table id="table1" :headers="headers" :items="customers" hide-default-footer
                    :server-items-length="totalItems" :items-per-page="itemsPerPage" :page.sync="currentPage"
                    :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" @update:page="onPageChange" item-key="_id"
                    @update:items-per-page="onItemsPerPageChange" @update:sort-by="onSortChange"
                    @update:sort-desc="onSortChange" show-expand :expanded.sync="expanded" @click:row="showManium"
                    class="cs-table">

                    <template v-slot:header.index="{ header }">
                        <span class="cs-th">{{ header.text }}</span>
                    </template>

                    <template v-for="header in headers" v-slot:[`header.${header.value}`]="{ header }">
                        <span class="cs-th">{{ header.text }}</span>
                    </template>

                    <template v-slot:item.index="{ index }">
                        <span class="cs-index">{{ (currentPage - 1) * itemsPerPage + index + 1 }}</span>
                    </template>

                    <template v-slot:item.name="{ item }">
                        <div class="cs-name-cell">
                            <span class="cs-avatar" :style="{ background: avatarColor(item) }">{{ initials(item)
                            }}</span>
                            <span class="cs-name-text">{{ item.name }}</span>
                        </div>
                    </template>

                    <template v-slot:item.card_id="{ item }">
                        <span @click.stop="() =>{}">{{ item.card_id || '—' }}</span>
                    </template>

                    <template v-slot:item.email="{ item }">
                        <span :class="['cs-cell-text', { 'cs-muted': !item.email }]">{{ item.email || '—' }}</span>
                    </template>

                    <template v-slot:item.phone1="{ item }">
                        <span class="cs-phone-text" @click.stop="() =>{}">{{ item.phone1 || '—' }}</span>
                    </template>

                    <template v-slot:item.community_id.name="{ item }">
                        <div class="cs-community-cell">
                            <span class="cs-community-badge">{{ item.community_id && item.community_id.name }}</span>
                            <v-tooltip bottom v-if="role === 'admin'">
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="cs-icon-btn"
                                        @click.stop="openEditCommunityDialog(item)" aria-label="עדכון קהילה">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" stroke-width="2.2" stroke-linecap="round"
                                            stroke-linejoin="round">
                                            <path d="M12 20h9" />
                                            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                                        </svg>
                                    </button>
                                </template>
                                <span>עדכון קהילה</span>
                            </v-tooltip>
                        </div>
                    </template>

                    <template v-slot:expanded-item="{ headers, item }">
                        <td :colspan="headers.length" class="cs-expanded-cell">
                            <div class="cs-expanded">
                                <div class="cs-expanded__grid">
                                    <div class="cs-detail">
                                        <div class="cs-detail__icon" data-tone="indigo">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2.2" stroke-linecap="round"
                                                stroke-linejoin="round">
                                                <path
                                                    d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z" />
                                            </svg>
                                        </div>
                                        <div class="cs-detail__body">
                                            <span class="cs-detail__label">טלפון נוסף</span>
                                            <span class="cs-detail__value">{{ item.phone2 || '—' }}</span>
                                        </div>
                                    </div>

                                    <div class="cs-detail">
                                        <div class="cs-detail__icon" data-tone="navy">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2.2" stroke-linecap="round"
                                                stroke-linejoin="round">
                                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                                <circle cx="12" cy="10" r="3" />
                                            </svg>
                                        </div>
                                        <div class="cs-detail__body">
                                            <span class="cs-detail__label">כתובת</span>
                                            <span class="cs-detail__value">{{ [item.address,
                                            item.building].filter(Boolean).join(' ') || '—' }}</span>
                                        </div>
                                    </div>

                                    <div class="cs-detail">
                                        <div class="cs-detail__icon" data-tone="purple">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2.2" stroke-linecap="round"
                                                stroke-linejoin="round">
                                                <rect x="3" y="8" width="18" height="13" rx="2" />
                                                <path d="m3 8 9-6 9 6" />
                                            </svg>
                                        </div>
                                        <div class="cs-detail__body">
                                            <span class="cs-detail__label">מיקוד</span>
                                            <span class="cs-detail__value">{{ item.postal_code || '—' }}</span>
                                        </div>
                                    </div>

                                    <div class="cs-detail">
                                        <div class="cs-detail__icon" data-tone="gold">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2.2" stroke-linecap="round"
                                                stroke-linejoin="round">
                                                <rect x="2" y="6" width="20" height="14" rx="2" />
                                                <line x1="2" y1="11" x2="22" y2="11" />
                                            </svg>
                                        </div>
                                        <div class="cs-detail__body">
                                            <span class="cs-detail__label">אמצעי תשלום</span>
                                            <span class="cs-detail__value">{{ item.last_digits ? '•••• ' +
                                                item.last_digits : '—' }}</span>
                                        </div>
                                    </div>

                                    <div class="cs-detail">
                                        <div class="cs-detail__icon" data-tone="indigo">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2.2" stroke-linecap="round"
                                                stroke-linejoin="round">
                                                <rect x="3" y="4" width="18" height="18" rx="2" />
                                                <line x1="16" y1="2" x2="16" y2="6" />
                                                <line x1="8" y1="2" x2="8" y2="6" />
                                                <line x1="3" y1="10" x2="21" y2="10" />
                                            </svg>
                                        </div>
                                        <div class="cs-detail__body">
                                            <span class="cs-detail__label">נוצר בתאריך</span>
                                            <span class="cs-detail__value">{{ item.createdAt || '—' }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </td>
                    </template>

                    <template v-slot:item.actions="{ item, index }">
                        <div class="cs-actions" @click.stop>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="cs-action-btn cs-action-btn--view"
                                        @click.stop="showManium(item)" aria-label="צפייה במנויים">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                            stroke-linejoin="round">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    </button>
                                </template>
                                <span>צפייה במנויים</span>
                            </v-tooltip>

                            <v-tooltip bottom v-if="isAdmin">
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="cs-action-btn cs-action-btn--edit"
                                        @click.stop="openEditDialog(item, index)" aria-label="עריכת לקוח">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                            stroke-linejoin="round">
                                            <path d="M12 20h9" />
                                            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                                        </svg>
                                    </button>
                                </template>
                                <span>עריכת לקוח</span>
                            </v-tooltip>

                            <span v-if="item.manuims && item.manuims.some(m => m.status === 'proccess')"
                                class="cs-status-pill">
                                <span class="cs-status-pill__dot"></span>
                                <span>{{item.manuims.filter(m => m.status === 'proccess').length}} בתהליך</span>
                            </span>
                        </div>
                    </template>

                    <template v-slot:no-data>
                        <div class="cs-empty">
                            <v-progress-linear v-if="progressShow" indeterminate color="#fdb721" height="3"
                                class="cs-empty__progress"></v-progress-linear>
                            <template v-else>
                                <div class="cs-empty__icon" aria-hidden="true">
                                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                                        <circle cx="11" cy="11" r="8" />
                                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                    </svg>
                                </div>
                                <p class="cs-empty__title">לא נמצאו לקוחות</p>
                                <p class="cs-empty__hint">נסה לשנות את הסינון או החיפוש</p>
                                <button type="button" class="cs-empty__btn" @click="getCustomers()">רענון</button>
                            </template>
                        </div>
                    </template>

                </v-data-table>

                <div class="cs-pagination-wrap" v-if="totalItems > itemsPerPage">
                    <v-pagination v-model="currentPage" circle :length="Math.ceil(totalItems / itemsPerPage)"
                        :total-visible="7"></v-pagination>
                </div>
            </div>
        </main>

        <template>
            <v-overlay :value="overlay">
                <v-progress-circular indeterminate size="84" color="#0d2c6d"></v-progress-circular>
            </v-overlay>
        </template>
        <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>

        <AddEditDialog v-model="addEditDialog" v-if="addEditDialog" :formTitle="formTitle" :itemToEdit="editedItem"
            @itemEdited="editedItemSaved" />

        <EditCommunityDialog v-model="editCommunityDialog" v-if="editCommunityDialog" :itemToEdit="editedItem"
            @itemEdited="editedItemSaved" />
    </div>
</template>

<script>

import SnackBar from '@/components/widgets/snackBar.vue'
import ApiServices from '@/services/api.service'
import Auth from '@/services/auth.service';
import Utils from "@/util/utils";
import AddEditDialog from '@/components/customers/dialogs/AddEditDialog'
import EditCommunityDialog from '@/components/customers/dialogs/EditCommunityDialog'
import _ from 'lodash'; // Import lodash or use another debounce utility

export default {

    components: {
        SnackBar,
        AddEditDialog,
        EditCommunityDialog,
    },
    data: () => ({
        headers: [
            { text: '#', value: 'index' },
            { text: 'שם', value: 'name' },
            { text: 'משפחה', value: 'family' },
            { text: 'ת.ז/ח.פ', value: 'card_id' },
            { text: 'שם לחשבונית', value: 'business_name' },
            { text: 'אימייל', value: 'email' },
            // { text: 'רחוב', value: 'address' },
            // { text: 'מספר בית', value: 'building' },
            { text: 'עיר', value: 'city' },
            { text: 'טלפון', value: 'phone1' },
            { text: 'קהילה', value: 'community_id.name' },
            { text: 'פעולות', value: 'actions' },
        ],
        overlay: false,
        customers: [],
        addEditDialog: false,
        editCommunityDialog: false,
        formTitle: "",
        editedItem: {},
        snackbar: false,
        snackbarColorBt: "green",
        snacbarText: "",
        search: "",
        progressShow: false,
        currentPage: 1,
        pageCount: 0,
        itemsPerPage: 20,
        page: 1, // Current page
        totalItems: 0, // Total number of customers for pagination controls
        totalFilterManuimsCount: 0,
        sortBy: "createdAt",
        sortDesc: true, // default sort direction
        isAdmin: false,
        expanded: [],
        filterType: "all",
        typesList: [{ hebrewName: "הצג הכל", value: "all" }, { hebrewName: "מנויים בתהליך", value: "proccess" }, { hebrewName: "מנויים פעילים", value: "active" },
        { hebrewName: "מנויים לא פעילים", value: "not_active" }],
        role: Auth.getUserRole()
    }),
    computed: {
    },
    methods: {
        async getCustomers(newSearch = false) {
            if (newSearch) {
                this.currentPage = 1;
            }
            try {
                this.progressShow = true;
                const token = localStorage.getItem("token");
                const api = process.env.VUE_APP_BASE_URL + `/customers/get_customers`;
                const query = new URLSearchParams({
                    page: this.currentPage,
                    itemsPerPage: this.itemsPerPage,
                    sortBy: this.sortBy,
                    sortDesc: this.sortDesc ? 'desc' : 'asc',
                    search: (this.search || '').replace(/[\u200E\u200F\u202A-\u202E]/g, '').trim(), // to delete hiden chars
                    filterType: this.filterType
                }).toString();

                const res = await fetch(`${api}?${query}`, ApiServices.requestOptions("GET", "", token));
                const jsonObject = await res.json();
                this.progressShow = false;

                if (res.status >= 400) {
                    this.showSnackBar("שגיאה בקבלת נתונים", "red");
                } else if (res.status === 200) {
                    this.customers = jsonObject.data; // Assuming `data` is the array of customers
                    this.totalItems = jsonObject.totalItems; // Assuming `totalItems` is returned by the API
                    this.totalFilterManuimsCount = jsonObject.totalFilterManuimsCount || 0;
                }
            } catch (error) {
                this.progressShow = false;
                this.showSnackBar("Error get files: " + error, "red");
            }
        },
        formatDetails() {
            this.customers.forEach(customer => {
                customer.createdAt = Utils.formatDateString(customer.createdAt)
            });
        },
        onPageChange(newPage) {
            this.currentPage = newPage;
            this.getCustomers(); // Fetch the new page
        },
        onItemsPerPageChange(newItemsPerPage) {
            this.itemsPerPage = newItemsPerPage;
            this.getCustomers(); // Fetch with updated items per page
        },
        onSortChange() {
            this.getCustomers(); // Fetch with updated sorting
        },
        showSnackBar(message, color) {
            this.snacbarText = message;
            this.snackbarColorBt = color;
            this.snackbar = true
        },
        showManium(customer) {
            console.log(customer)
            this.$router.push({ name: "manuim", params: { id: customer._id, name: customer.name + " " + customer.family, cardId: customer.card_id } })
        },
        openEditDialog(item, index) {
            this.editedItem = item;
            this.formTitle = "ערוך פרטי לקוח"
            this.addEditDialog = true
        },
        editedItemSaved(item, text, color) {
            this.showSnackBar(text, color)
            if (item === "") {
                return
            }
            const index = this.customers.findIndex(cust => cust._id === item._id);
            Object.assign(this.customers[index], item);
        },
        openEditCommunityDialog(item) {
            this.editedItem = item;
            this.editCommunityDialog = true
        },
        async exportFiltersManuim() {
            try {
                this.progressShow = true;
                let token = localStorage.getItem("token");
                let api = process.env.VUE_APP_BASE_URL + `/manuim/export_filter_manuim_to_excel?filterType=${this.filterType}`;
                const res = await fetch(api, ApiServices.requestOptions("GET", "", token));
                this.progressShow = false;
                if (res.status >= 400) {
                    this.showSnackBar("שגיאה בקבלת נתונים", "red");
                } else if (res.status === 200) {
                    // Convert response to Blob for download
                    const blob = await res.blob();
                    // Create a link element for downloading the Blob
                    const downloadUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = downloadUrl;
                    link.download = this.filterType + "_Manuim.xlsx"; // Set the desired file name
                    document.body.appendChild(link);
                    link.click(); // Programmatically click the link to trigger download
                    link.remove(); // Remove the link after download
                    // Revoke the object URL after download
                    window.URL.revokeObjectURL(downloadUrl);
                    this.showSnackBar("File downloaded successfully", "green");
                }
            } catch (error) {
                this.progressShow = false;
                console.log(error);
                this.showSnackBar("Error get files: " + error, "red");
            }
        },
        handleSearchInput() {
            if (this.search) {
                this.debouncedGetCustomers(true)
            } else {
                this.getCustomers(true)
            }
        },
        // ── Presentation helpers (no side effects, used by template) ──
        initials(item) {
            const n = ((item && item.name) || '').trim().charAt(0);
            const f = ((item && item.family) || '').trim().charAt(0);
            return (n + f).toUpperCase() || '?';
        },
        avatarColor(item) {
            const seed = String((item && (item.card_id || item._id || item.name)) || '');
            const palette = ['#0d2c6d', '#3F51B5', '#8066f0', '#1976d2', '#0c8e6f', '#c0691c'];
            let hash = 0;
            for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
            return palette[Math.abs(hash) % palette.length];
        },
    },
    created() {
        this.getCustomers();
        this.isAdmin = Auth.getUserRole() === "admin" ? true : false;

        // Create a debounced version of getData to avoid multiple rapid calls
        this.debouncedGetCustomers = _.debounce(this.getCustomers, 700);
    },
}
</script>
<style scoped>
/* ─────────────────────── TOKENS ─────────────────────── */
.cs-page {
    --c-navy: #0d2c6d;
    --c-navy-soft: #142f78;
    --c-indigo: #3F51B5;
    --c-purple: #8066f0;
    --c-gold: #fdb721;
    --c-blue: #1976d2;
    --c-bg: #f3f6f9;
    --c-surface: #ffffff;
    --c-border: #e3e8ef;
    --c-border-soft: #eef2f7;
    --c-text: #0f172a;
    --c-text-soft: #475569;
    --c-text-mute: #94a3b8;
    --c-warn: #c0691c;
    --c-warn-soft: #fff3df;

    --shadow-sm: 0 1px 2px rgba(13, 44, 109, 0.04), 0 1px 3px rgba(13, 44, 109, 0.06);
    --shadow-md: 0 4px 14px rgba(13, 44, 109, 0.07), 0 2px 4px rgba(13, 44, 109, 0.04);
    --shadow-lg: 0 18px 40px -12px rgba(13, 44, 109, 0.18), 0 8px 16px -8px rgba(13, 44, 109, 0.10);
    --ease: cubic-bezier(0.22, 1, 0.36, 1);

    font-family: 'Assistant', 'Heebo', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    direction: rtl;
    background: var(--c-bg);
    min-height: 100vh;
    padding-bottom: 60px;
}

/* ─────────────────────── HEADER ─────────────────────── */
.cs-header {
    background: linear-gradient(135deg, #0d2c6d 0%, #142f78 55%, #1a3a8a 100%);
    color: #fff;
    padding: 12px 0 14px;
    box-shadow: var(--shadow-md);
}

.cs-header__inner {
    max-width: 1500px;
    margin: 0 auto;
    padding: 0 28px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.cs-header__title-wrap {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}

.cs-title {
    margin: 0;
    font-size: 1.3rem;
    font-weight: 800;
    letter-spacing: -0.01em;
    line-height: 1.2;
}

.cs-total-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.10);
    border: 1px solid rgba(255, 255, 255, 0.20);
    font-size: 0.85rem;
    font-weight: 700;
    color: #fff;
    font-feature-settings: "tnum" 1;
}

.cs-total-pill svg {
    opacity: 0.85;
}

.cs-filter-summary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 4px 6px 4px 14px;
    border-radius: 999px;
    background: rgba(253, 183, 33, 0.16);
    border: 1px solid rgba(253, 183, 33, 0.30);
    color: #fff;
    font-size: 0.82rem;
    flex-shrink: 0;
}

.cs-filter-summary__count {
    font-weight: 800;
    color: var(--c-gold);
    font-size: 0.95rem;
    font-feature-settings: "tnum" 1;
}

.cs-filter-summary__label {
    opacity: 0.85;
}

.cs-export-btn {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: var(--c-gold);
    color: #2d1f00;
    border: none;
    cursor: pointer;
    transition: transform 0.2s var(--ease), filter 0.2s var(--ease);
}

.cs-export-btn:hover {
    transform: scale(1.08);
    filter: brightness(1.05);
}

/* ─────────────────────── MAIN WRAP ─────────────────────── */
.cs-main {
    max-width: 1500px;
    margin: 22px auto 0;
    padding: 0 22px;
}

/* ─────────────────────── TOOLBAR ─────────────────────── */
.cs-toolbar {
    display: grid;
    grid-template-columns: minmax(220px, 360px) 1fr auto;
    align-items: center;
    gap: 14px;
    margin-bottom: 18px;
}

.cs-search {
    position: relative;
    display: flex;
    align-items: center;
    background: var(--c-surface);
    border: 1px solid var(--c-border);
    border-radius: 12px;
    padding: 0 12px;
    height: 42px;
    transition: border-color 0.2s var(--ease), box-shadow 0.2s var(--ease);
}

.cs-search:focus-within {
    border-color: var(--c-navy);
    box-shadow: 0 0 0 4px rgba(13, 44, 109, 0.08);
}

.cs-search__icon {
    color: var(--c-text-mute);
    flex-shrink: 0;
}

.cs-search__input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-family: inherit;
    font-size: 0.94rem;
    color: var(--c-text);
    padding: 0 10px;
}

.cs-search__input::placeholder {
    color: var(--c-text-mute);
}

.cs-search__clear {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: none;
    background: var(--c-border-soft);
    color: var(--c-text-soft);
    cursor: pointer;
    display: grid;
    place-items: center;
    transition: background 0.2s var(--ease);
}

.cs-search__clear:hover {
    background: var(--c-border);
}

.cs-filter-chips {
    display: flex;
    align-items: center;
    gap: 8px;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    flex-wrap: wrap;
}

.cs-filter-chips::-webkit-scrollbar {
    display: none;
}

.cs-chip {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 7px 14px;
    border-radius: 999px;
    background: var(--c-surface);
    border: 1px solid var(--c-border);
    color: var(--c-text-soft);
    font-family: inherit;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.2s var(--ease);
}

.cs-chip:hover {
    border-color: rgba(13, 44, 109, 0.25);
    color: var(--c-text);
}

.cs-chip__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--c-border);
    transition: background 0.2s var(--ease);
}

.cs-chip--active {
    background: var(--c-navy);
    border-color: var(--c-navy);
    color: #fff;
    box-shadow: 0 6px 14px -4px rgba(13, 44, 109, 0.4);
}

.cs-chip--active .cs-chip__dot {
    background: var(--c-gold);
}

.cs-pagesize {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: var(--c-text-soft);
    font-size: 0.85rem;
    font-weight: 600;
    flex-shrink: 0;
}

.cs-pagesize__select {
    padding: 7px 12px;
    border-radius: 10px;
    border: 1px solid var(--c-border);
    background: var(--c-surface);
    font-family: inherit;
    font-size: 0.88rem;
    color: var(--c-text);
    font-weight: 600;
    cursor: pointer;
    transition: border-color 0.2s var(--ease);
}

.cs-pagesize__select:hover {
    border-color: rgba(13, 44, 109, 0.25);
}

.cs-pagesize__select:focus {
    outline: none;
    border-color: var(--c-navy);
    box-shadow: 0 0 0 4px rgba(13, 44, 109, 0.08);
}

/* ─────────────────────── TABLE CARD ─────────────────────── */
.cs-table-card {
    background: var(--c-surface);
    border-radius: 18px;
    border: 1px solid var(--c-border-soft);
    box-shadow: var(--shadow-md);
    overflow: hidden;
}

/* Vuetify data-table overrides — ::v-deep penetrates into Vuetify-generated DOM */
::v-deep #table1.v-data-table {
    background: transparent !important;
}

::v-deep #table1.v-data-table .v-data-table-header th {
    background: #fbfcfe !important;
    height: 52px !important;
    text-align: center !important;
    border-bottom: 1px solid var(--c-border-soft) !important;
    border-left: none !important;
}

::v-deep #table1.v-data-table .v-data-table-header th:first-child {
    padding-right: 18px;
}

::v-deep #table1.v-data-table .v-data-table-header th .v-data-table-header__icon {
    color: var(--c-text-mute) !important;
    opacity: 1 !important;
}

.cs-th {
    font-family: inherit !important;
    font-size: 0.82rem !important;
    font-weight: 700 !important;
    color: var(--c-text-soft) !important;
    letter-spacing: 0.02em;
    text-transform: uppercase;
}

::v-deep #table1.v-data-table tbody tr {
    cursor: pointer;
    transition: background 0.18s var(--ease);
}

::v-deep #table1.v-data-table tbody tr:hover:not(.v-data-table__expanded__content) {
    background: #f8fafc !important;
}

::v-deep #table1.v-data-table tbody tr td {
    border-bottom: 1px solid var(--c-border-soft) !important;
    text-align: center;
    font-family: inherit;
    font-size: 0.9rem;
    color: var(--c-text);
    padding: 12px 14px !important;
    height: 60px;
}

::v-deep #table1.v-data-table tbody tr.v-data-table__expanded__row {
    background: #f8fafc !important;
}

::v-deep #table1.v-data-table tbody tr.v-data-table__expanded__row td {
    border-bottom: none !important;
}

/* expand toggle icon */
::v-deep #table1.v-data-table .v-data-table__expand-icon {
    color: var(--c-text-mute) !important;
    background: var(--c-border-soft) !important;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    transition: all 0.2s var(--ease) !important;
}

::v-deep #table1.v-data-table .v-data-table__expand-icon:hover {
    color: var(--c-navy) !important;
    background: rgba(13, 44, 109, 0.08) !important;
}

/* ─────────────────────── NAME CELL ─────────────────────── */
.cs-name-cell {
    display: flex;
    align-items: center;
    gap: 10px;
    justify-content: flex-start;
    min-width: 0;
}

.cs-avatar {
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    color: #fff;
    font-weight: 800;
    font-size: 0.82rem;
    letter-spacing: 0.02em;
    box-shadow: 0 2px 8px -2px rgba(13, 44, 109, 0.25);
    user-select: none;
}

.cs-name-text {
    font-weight: 600;
    color: var(--c-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cs-index {
    color: var(--c-text-mute);
    font-weight: 600;
    font-size: 0.85rem;
    font-feature-settings: "tnum" 1;
}

.cs-cell-text {
    color: var(--c-text);
}

.cs-muted {
    color: var(--c-text-mute);
}

.cs-phone-text {
    font-feature-settings: "tnum" 1;
    color: var(--c-text);
    direction: ltr;
    display: inline-block;
}

/* ─────────────────────── COMMUNITY CELL ─────────────────────── */
.cs-community-cell {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    justify-content: center;
}

.cs-community-badge {
    display: inline-block;
    padding: 4px 11px;
    border-radius: 999px;
    background: rgba(63, 81, 181, 0.10);
    color: var(--c-indigo);
    font-weight: 700;
    font-size: 0.8rem;
    white-space: nowrap;
}

.cs-icon-btn {
    width: 26px;
    height: 26px;
    border-radius: 7px;
    background: transparent;
    border: none;
    color: var(--c-text-mute);
    cursor: pointer;
    display: grid;
    place-items: center;
    transition: all 0.18s var(--ease);
}

.cs-icon-btn:hover {
    background: var(--c-border-soft);
    color: var(--c-navy);
}

/* ─────────────────────── ACTIONS ─────────────────────── */
.cs-actions {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    justify-content: center;
}

.cs-action-btn {
    width: 32px;
    height: 32px;
    border-radius: 9px;
    border: 1px solid var(--c-border);
    background: var(--c-surface);
    color: var(--c-text-soft);
    cursor: pointer;
    display: grid;
    place-items: center;
    transition: all 0.18s var(--ease);
}

.cs-action-btn:hover {
    transform: translateY(-1px);
    border-color: transparent;
    color: #fff;
}

.cs-action-btn--view:hover {
    background: var(--c-navy);
    box-shadow: 0 4px 10px -2px rgba(13, 44, 109, 0.4);
}

.cs-action-btn--edit:hover {
    background: var(--c-indigo);
    box-shadow: 0 4px 10px -2px rgba(63, 81, 181, 0.4);
}

.cs-status-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px 4px 8px;
    margin-right: 4px;
    border-radius: 999px;
    background: var(--c-warn-soft);
    color: var(--c-warn);
    font-weight: 700;
    font-size: 0.76rem;
    white-space: nowrap;
}

.cs-status-pill__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--c-warn);
    box-shadow: 0 0 0 4px rgba(192, 105, 28, 0.18);
    animation: cs-pulse 1.8s ease-in-out infinite;
}

@keyframes cs-pulse {

    0%,
    100% {
        box-shadow: 0 0 0 4px rgba(192, 105, 28, 0.18);
    }

    50% {
        box-shadow: 0 0 0 6px rgba(192, 105, 28, 0.04);
    }
}

/* ─────────────────────── EXPANDED ROW ─────────────────────── */
.cs-expanded-cell {
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%) !important;
    padding: 0 !important;
    box-shadow: inset 0 6px 12px -8px rgba(13, 44, 109, 0.16);
}

.cs-expanded {
    padding: 18px 22px 22px;
    animation: cs-fadeIn 0.32s var(--ease);
}

@keyframes cs-fadeIn {
    from {
        opacity: 0;
        transform: translateY(-4px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.cs-expanded__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
}

.cs-detail {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    background: var(--c-surface);
    border: 1px solid var(--c-border-soft);
    border-radius: 12px;
    text-align: right;
    transition: border-color 0.2s var(--ease), transform 0.2s var(--ease);
}

.cs-detail:hover {
    border-color: rgba(13, 44, 109, 0.18);
    transform: translateY(-1px);
}

.cs-detail__icon {
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    border-radius: 9px;
    display: grid;
    place-items: center;
    color: #fff;
}

.cs-detail__icon[data-tone="navy"] {
    background: linear-gradient(135deg, var(--c-navy), var(--c-navy-soft));
}

.cs-detail__icon[data-tone="indigo"] {
    background: linear-gradient(135deg, var(--c-indigo), #4c5fc7);
}

.cs-detail__icon[data-tone="purple"] {
    background: linear-gradient(135deg, var(--c-purple), #6b54e0);
}

.cs-detail__icon[data-tone="gold"] {
    background: linear-gradient(135deg, #f0a30e, var(--c-gold));
    color: #4a3500;
}

.cs-detail__body {
    display: flex;
    flex-direction: column;
    min-width: 0;
    gap: 2px;
}

.cs-detail__label {
    font-size: 0.72rem;
    font-weight: 700;
    color: var(--c-text-mute);
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.cs-detail__value {
    font-size: 0.94rem;
    font-weight: 600;
    color: var(--c-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* ─────────────────────── EMPTY STATE ─────────────────────── */
.cs-empty {
    text-align: center;
    padding: 56px 24px;
}

.cs-empty__progress {
    max-width: 240px;
    margin: 0 auto;
    border-radius: 999px;
}

.cs-empty__icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 16px;
    border-radius: 50%;
    background: rgba(13, 44, 109, 0.06);
    color: var(--c-navy);
    display: grid;
    place-items: center;
}

.cs-empty__title {
    font-size: 1.05rem;
    font-weight: 700;
    color: var(--c-text);
    margin: 0 0 4px;
}

.cs-empty__hint {
    color: var(--c-text-mute);
    font-size: 0.88rem;
    margin: 0 0 18px;
}

.cs-empty__btn {
    padding: 10px 26px;
    border: none;
    border-radius: 10px;
    background: var(--c-navy);
    color: #fff;
    font-family: inherit;
    font-weight: 700;
    font-size: 0.9rem;
    cursor: pointer;
    transition: all 0.2s var(--ease);
    box-shadow: 0 6px 14px -4px rgba(13, 44, 109, 0.4);
}

.cs-empty__btn:hover {
    transform: translateY(-1px);
    filter: brightness(1.08);
}

/* ─────────────────────── PAGINATION ─────────────────────── */
.cs-pagination-wrap {
    padding: 18px 12px 12px;
    border-top: 1px solid var(--c-border-soft);
    display: flex;
    justify-content: center;
}

::v-deep .cs-pagination-wrap .v-pagination__item {
    font-family: inherit !important;
    font-weight: 700 !important;
    box-shadow: none !important;
    border: 1px solid var(--c-border) !important;
    color: var(--c-text-soft) !important;
}

::v-deep .cs-pagination-wrap .v-pagination__item--active {
    background: var(--c-navy) !important;
    color: #fff !important;
    border-color: var(--c-navy) !important;
    box-shadow: 0 4px 10px -2px rgba(13, 44, 109, 0.4) !important;
}

::v-deep .cs-pagination-wrap .v-pagination__navigation {
    box-shadow: none !important;
    border: 1px solid var(--c-border) !important;
}

/* ─────────────────────── RESPONSIVE ─────────────────────── */
@media (max-width: 1000px) {
    .cs-toolbar {
        grid-template-columns: 1fr;
        gap: 10px;
    }

    .cs-filter-chips {
        order: 3;
    }

    .cs-pagesize {
        order: 2;
        justify-content: flex-end;
    }
}

@media (max-width: 640px) {
    .cs-header__inner {
        padding: 0 18px;
    }

    .cs-main {
        padding: 0 12px;
    }

    .cs-title {
        font-size: 1.1rem;
    }

    .cs-filter-summary__label {
        display: none;
    }

    .cs-expanded {
        padding: 14px;
    }

    .cs-name-text {
        font-size: 0.88rem;
    }

    #table1.v-data-table tbody tr td {
        padding: 10px 8px !important;
        font-size: 0.85rem;
    }
}

@media (prefers-reduced-motion: reduce) {

    .cs-status-pill__dot,
    .cs-expanded {
        animation: none;
    }
}
</style>